import React, { FC, useRef, useState } from 'react'
import { ReviewersProps as Props } from './types'
import { ActionsColumn, AutocompleteInput, FILTER_TYPE, DataTable, Item, NestedMenu, NotificationType, Title, Grid } from 'prace-common-components'
import { $Grid, $SvgIcon } from './styles'
import { useAddCenterReviewersMutation, useGetReviewersQuery, useRemoveCenterReviewerMutation } from 'store/api/partitions'
import { useSearchUsersMutation } from 'store/api/users'
import { useAppDispatch } from 'store/hooks'
import { UserSuggestion } from 'store/api/users/types'

const menuItems = [
  {id: 1, label: <><$SvgIcon name='delete' size={16} />Remove</>},
]

const columns = [
  { key: 'name', noSort: true, type: FILTER_TYPE.NONE, name: 'Name' },
  { key: 'email', noSort: true, type: FILTER_TYPE.NONE, name: 'Email' },
  { key: 'actions', noSort: true, type: FILTER_TYPE.NONE, name: 'Actions', width: 35 },
]

export const Reviewers: FC<Props> = ({ centerId }) => {
  const dispatch = useAppDispatch()
  const searchTimer = useRef<number>()
  const [suggestionData, setSuggestionData] = useState<UserSuggestion[]>([])
  const [selectedReviewer, setSelectedReviewer] = useState<Item>({ value: 0, label: '' })
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [selectedId, setSelectedId] = React.useState<number>()

  const { data: reviewers = [] } = useGetReviewersQuery({ centerId })
  const [searchUsers] = useSearchUsersMutation()
  const [addCenterReviewers] = useAddCenterReviewersMutation()
  const [removeCenterReviewer] = useRemoveCenterReviewerMutation()

  const reviewerIds = reviewers.map((u) => u.id)

  const suggestionItems: Item[] = suggestionData
    .filter((s) => !reviewerIds.includes(s.id))
    .map((s) => ({ label: `${s.firstName} ${s.lastName} - ${s.email}`, value: s.id }))

  const rows = reviewers.map((user) => ({
    ...user,
    name: `${user.firstName} ${user.lastName}`,
    actions: <ActionsColumn handleClick={(e: React.MouseEvent<any>) => {
      setAnchorEl(e.currentTarget)
      setSelectedId(user.id)
    }} />,
  }))

  const handleRemove = async () => {
    setAnchorEl(null)
    if (selectedId === undefined) return
    try {
      await removeCenterReviewer({ centerId, userId: selectedId }).unwrap()
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'Reviewer removed' } })
    } catch (err) {
      console.log(err)
    }
  }

  const onInputChange = (_: any, value: string) => {
    clearTimeout(searchTimer.current)
    searchTimer.current = window.setTimeout(async () => {
      try {
        const { users } = await searchUsers({ search: value, page: 0, pageSize: 999 }).unwrap()
        setSuggestionData(users as unknown as UserSuggestion[])
      } catch (err) {
        console.log(err)
      }
    }, 500)
  }

  const handleAdd = async (_: any, item: unknown) => {
    const selected = item as Item
    if (!selected?.value) return
    const user = suggestionData.find((s) => s.id === Number(selected.value))
    if (!user) return
    try {
      await addCenterReviewers({ centerId, userIds: [user.id] }).unwrap()
      setSelectedReviewer({ value: 0, label: '' })
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'Reviewer added' } })
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <$Grid container>
      <Grid container alignItems='center' justifyContent='space-between'>
        <Grid item xs={4}>
          <Title>Reviewers</Title>
        </Grid>
        <Grid item xs={8}>
          <AutocompleteInput
            name='reviewer'
            placeholder='Search to add a reviewer'
            items={suggestionItems}
            value={selectedReviewer}
            onChange={handleAdd}
            onInputChange={onInputChange}
          />
        </Grid>
      </Grid>
      <DataTable
        showEmptyImg={false}
        noFilters
        rows={rows}
        columns={columns}
      />
      <NestedMenu
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        items={menuItems}
        handleItemClick={handleRemove}
      />
    </$Grid>
  )
}
